/**
 * Stato di "Scrivi a piu' persone": destinatari, numero, messaggio, invio.
 * La logica sta nei moduli puri di src/lib/services/whatsapp/bulkSend/; qui
 * solo caricamenti e collegamenti.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useUserData } from '@/lib/auth';
import { usePermissions } from '@/lib/hooks/usePermissions';
import { usePermissionsStore } from '@/lib/stores/permissionsStore';
import { useWhatsAppConfigs } from '@/lib/hooks/useWhatsAppConfigs';
import { useWhatsAppChatStore } from '@/lib/stores/whatsappChatStore';
import { buildRecipients, type BulkCandidate, type KnownChats } from '@/lib/services/whatsapp/bulkSend/bulkRecipients';
import { channelOf, computeSendTimes, firstSendTime } from '@/lib/services/whatsapp/bulkSend/bulkSchedule';
import {
  bulkTemplateBlockReason, buildScheduledRows, standardTextBlockReason, NAME_TOKEN,
} from '@/lib/services/whatsapp/bulkSend/bulkMessage';
import { describeTemplate } from '@/lib/services/whatsapp/templateSend';
import {
  createBulkSend, loadApprovedTemplates, loadClientCandidates, loadKnownChats, loadLabelCandidates, loadStageCandidates,
  type ApprovedTemplate,
} from '@/lib/services/whatsapp/bulkSend/bulkSendService';
import { normalizeWhatsAppDigits } from '@/lib/services/whatsapp/phoneDigits';

export type AudienceSource = 'selezione' | 'fase' | 'etichetta';
const EMPTY_KNOWN: KnownChats = { inOrg: new Set(), onConfig: new Set() };

export function useBulkSend(preselectedClientIds: string[] | undefined) {
  const { organizationId, userId } = useUserData();
  const { configs, loading: configsLoading } = useWhatsAppConfigs();
  const leadsPerm = usePermissions('leads');
  const hasSelection = !!preselectedClientIds && preselectedClientIds.length > 0;

  const [source, setSource] = useState<AudienceSource>(hasSelection ? 'selezione' : 'fase');
  const [pipelineId, setPipelineId] = useState('');
  const [stageId, setStageId] = useState('');
  const [labelId, setLabelId] = useState('');
  const [audienceLabel, setAudienceLabel] = useState(hasSelection ? `${preselectedClientIds!.length} Lead selezionati` : '');
  const [candidates, setCandidates] = useState<BulkCandidate[]>([]);
  const [loadingCandidates, setLoadingCandidates] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);

  const [configId, setConfigId] = useState<string | null>(null);
  const [known, setKnown] = useState<KnownChats>(EMPTY_KNOWN);
  const [text, setText] = useState(`Ciao ${NAME_TOKEN}, `);
  const [templates, setTemplates] = useState<ApprovedTemplate[]>([]);
  const [templateId, setTemplateId] = useState('');
  const [vars, setVars] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);

  // Senza accesso ai Lead la fase della pipeline non si puo' usare: si parte dalle etichette.
  useEffect(() => {
    if (source === 'fase' && !leadsPerm.loading && !leadsPerm.canRead) setSource('etichetta');
  }, [source, leadsPerm.loading, leadsPerm.canRead]);

  // Numero predefinito: quello aperto in chat, se l'utente lo vede.
  useEffect(() => {
    if (configId || configs.length === 0) return;
    const attivo = useWhatsAppChatStore.getState().wahaConfig?.configId;
    setConfigId(configs.find((c) => c.id === attivo)?.id ?? configs[0].id);
  }, [configs, configId]);

  const config = configs.find((c) => c.id === configId) ?? null;
  const channel = channelOf(config?.serviceName);

  // Destinatari dalla sorgente scelta.
  useEffect(() => {
    if (!organizationId) return;
    let annullato = false;
    const run = async (): Promise<BulkCandidate[]> => {
      if (source === 'selezione') return hasSelection ? loadClientCandidates(organizationId, preselectedClientIds!) : [];
      if (source === 'fase') {
        if (!pipelineId || !stageId || !leadsPerm.canRead) return [];
        return loadStageCandidates(organizationId, pipelineId, stageId, {
          visibility: usePermissionsStore.getState().getLeadVisibility(),
          // "Vedi solo i tuoi": qui solo i propri, per restare prudenti.
          onlyAssignedTo: leadsPerm.canFilterByUser ? userId : null,
        });
      }
      return labelId ? loadLabelCandidates(organizationId, labelId) : [];
    };
    setLoadingCandidates(true);
    setLoadError(null);
    run()
      .then((c) => { if (!annullato) setCandidates(c); })
      .catch(() => { if (!annullato) { setCandidates([]); setLoadError('Non riesco a caricare i destinatari. Riprova.'); } })
      .finally(() => { if (!annullato) setLoadingCandidates(false); });
    return () => { annullato = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [organizationId, source, pipelineId, stageId, labelId, leadsPerm.canRead, leadsPerm.canFilterByUser, userId]);

  // Chat gia' esistenti per i numeri dei candidati.
  useEffect(() => {
    if (!organizationId || !configId || candidates.length === 0) { setKnown(EMPTY_KNOWN); return; }
    let annullato = false;
    const digits = candidates
      .map((c) => normalizeWhatsAppDigits(c.whatsappNumber || c.phone || c.chatJid || '').replace(/\D/g, ''))
      .filter(Boolean);
    loadKnownChats(organizationId, configId, digits)
      .then((k) => { if (!annullato) setKnown(k); })
      .catch(() => { if (!annullato) setKnown(EMPTY_KNOWN); });
    return () => { annullato = true; };
  }, [organizationId, configId, candidates]);

  // Modelli approvati del numero Ufficiale.
  useEffect(() => {
    setTemplateId('');
    setVars({});
    if (!configId || channel !== 'ufficiale') { setTemplates([]); return; }
    let annullato = false;
    loadApprovedTemplates(configId).then((t) => { if (!annullato) setTemplates(t); }).catch(() => { if (!annullato) setTemplates([]); });
    return () => { annullato = true; };
  }, [configId, channel]);

  const template = templates.find((t) => t.id === templateId) ?? null;
  const shape = useMemo(() => (template ? describeTemplate(template.components) : null), [template]);

  // Nuovo modello: {{nome}} gia' nella prima variabile del corpo.
  const pickTemplate = useCallback((id: string) => {
    setTemplateId(id);
    const t = templates.find((x) => x.id === id);
    const first = t ? describeTemplate(t.components).fields.find((f) => f.component === 'body') : undefined;
    setVars(first ? { [first.key]: NAME_TOKEN } : {});
  }, [templates]);

  const built = useMemo(() => buildRecipients(candidates, channel, known), [candidates, channel, known]);

  const messageBlock: string | null = !config
    ? 'Scegli il numero da cui inviare.'
    : channel === 'standard'
      ? standardTextBlockReason(text)
      : !shape
        ? 'Scegli un modello approvato.'
        : bulkTemplateBlockReason(shape, vars, built.recipients);

  const submit = useCallback(async (): Promise<string> => {
    if (!organizationId || !userId || !config) throw new Error('Sessione non valida: ricarica la pagina.');
    if (built.recipients.length === 0) throw new Error('Nessun destinatario valido.');
    if (messageBlock) throw new Error(messageBlock);
    setSubmitting(true);
    try {
      const times = computeSendTimes(built.recipients.length, firstSendTime(new Date()), channel);
      const tpl = template ? { name: template.name, language: template.language, components: template.components } : null;
      return await createBulkSend(
        {
          organization_id: organizationId,
          config_id: config.id,
          created_by: userId,
          channel,
          message_text: channel === 'standard' ? text.trim() : null,
          template_name: tpl?.name ?? null,
          template_language: tpl?.language ?? null,
          audience_label: audienceLabel || 'Destinatari scelti',
          total_recipients: built.recipients.length,
          excluded_count: built.excluded.length,
          excluded_summary: built.summary as Record<string, number>,
          first_send_at: times[0].toISOString(),
          last_send_at: times[times.length - 1].toISOString(),
        },
        (bulkSendId) => buildScheduledRows({
          organizationId, configId: config.id, userId, bulkSendId, channel,
          recipients: built.recipients, times, text: text.trim(), template: tpl, vars,
        }),
      );
    } finally {
      setSubmitting(false);
    }
  }, [organizationId, userId, config, built, messageBlock, channel, template, text, audienceLabel, vars]);

  return {
    organizationId, configs, configsLoading, config, configId, setConfigId, channel,
    hasSelection, canUseStages: !!leadsPerm.canRead,
    source, setSource, pipelineId, setPipelineId, stageId, setStageId, labelId, setLabelId, setAudienceLabel,
    candidates, loadingCandidates, loadError, built,
    text, setText, templates, template, templateId, pickTemplate, shape, vars, setVars,
    messageBlock, submit, submitting,
  };
}

export type BulkSendState = ReturnType<typeof useBulkSend>;
